(ns io.daskul.tutorial.webapp.components.session-attendee-add
  "Custom 'Add Attendee' panel for the Session → Attendees child table.

   A session attendee is EITHER a lead-attendee (:lead-id set) or an
   enrollment-attendee (:enrollment-id set) — never both, never neither.
   The XOR is enforced server-side (DB CHECK + business-rules-fn), so the
   default child form, which renders BOTH lookups side-by-side, is a trap:
   the admin can pick both (→ 409) or neither (→ 409) and only finds out on
   submit. This panel makes the either/or explicit with a segmented
   [Lead | Enrollment] toggle that shows exactly one lookup at a time, so an
   illegal payload is impossible to construct from the UI. Switching the
   toggle clears any previously-picked id so only the active one is ever sent.

   Registered via aggregate/configure! as
     :custom-child-add-components {[:session :session-attendee] add-panel}"
  (:require
   [uix.core :as uix :refer [defui $]]
   [re-frame.core :as rf]
   [uix.re-frame :as urf]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.widgets.form.field :as ff]
   [io.daskul.webapp.widgets.child-aggregates :as child-agg]))

(def ^:private kind-options
  [{:value "lead" :label "Lead"}
   {:value "enrollment" :label "Enrollment"}])

(defn- field-by-name [fields fname]
  (some #(when (= fname (:name %)) %) fields))

(defn- default-form-data
  "Seed add-mode form data from each field's :default (mirrors child-inline-form)."
  [fields]
  (->> fields
       (keep (fn [field]
               (when (some? (:default field))
                 [(:name field) (:default field)])))
       (into {})))

(defui add-panel
  [{:keys [parent-agg-key parent-id child-agg-key fk-field on-close inline-form-props]}]
  (let [{:keys [schema item form-field-component]} inline-form-props
        fields (:fields schema)
        render-field (or form-field-component child-agg/default-form-field)
        saving? (urf/use-subscribe [::agg.subs/child-saving? parent-agg-key parent-id child-agg-key])
        form-error (urf/use-subscribe [::agg.subs/child-form-error parent-agg-key parent-id child-agg-key])

        [kind set-kind!] (uix/use-state "lead")
        [form-data set-form-data!] (uix/use-state (or (not-empty item)
                                                      (default-form-data fields)))

        id-field (if (= kind "lead") :lead-id :enrollment-id)
        id-field-def (field-by-name fields id-field)
        extra-fields (->> fields
                          (remove #(#{fk-field :state :lead-id :enrollment-id} (:name %))))

        render-props
        (fn [field]
          (let [field-key (:name field)
                raw-err (get-in form-error [:field-errors field-key])]
            {:key (name field-key)
             :agg-key child-agg-key
             :field field
             :value (get form-data field-key)
             :on-change #(set-form-data! (assoc form-data field-key %))
             :form-data form-data
             :external-error (ff/humanize-error raw-err)
             :on-clear-error #(rf/dispatch [::agg.events/clear-child-field-error
                                            parent-agg-key parent-id child-agg-key field-key])}))

        handle-kind
        (fn [v]
          (set-kind! v)
          (set-form-data! (dissoc form-data :lead-id :enrollment-id)))

        handle-submit
        (fn [e]
          (.preventDefault e)
          (.stopPropagation e)
          (let [clean (select-keys form-data (into [id-field] (map :name extra-fields)))]
            (rf/dispatch [::agg.events/create-child parent-agg-key parent-id
                          child-agg-key fk-field clean])))

        error-msg (ff/form-level-error-message
                   form-error
                   (remove #(#{fk-field :state} (:name %)) fields))]
    ($ ui/box {:p "md"
               :style {:backgroundColor "var(--mantine-color-gray-0)"
                       :borderRadius "var(--mantine-radius-sm)"}}
      ($ :form {:onSubmit handle-submit}
        ($ ui/stack {:gap "sm"}
          (when error-msg
            ($ ui/error-alert {:mb "xs"} error-msg))
          ($ ui/stack {:gap 4}
            ($ ui/text {:size "sm" :fw 500} "Attendee type")
            ($ ui/segmented-control
              {:value kind :onChange handle-kind :fullWidth true
               :data (clj->js kind-options)}))
          (when id-field-def
            ($ render-field (render-props id-field-def)))
          (for [field extra-fields]
            ($ render-field (render-props field)))
          ($ ui/group {:justify "flex-end" :gap "sm"}
            ($ ui/button {:variant "subtle" :color "gray" :size "sm" :onClick on-close}
              "Cancel")
            ($ ui/button {:type "submit" :size "sm" :loading saving?}
              "Add")))))))
